import type { AsyncStorage, PersistenceSyncAPI, SyncStorage } from "@solid-primitives/storage"
import { getOwner, onCleanup, untrack } from "solid-js"
import { reconcile, type SetStoreFunction, type Store } from "solid-js/store"

export const persistSaveDelay = 100

const pending = new Set<() => void>()
const writing = new Set<Promise<void>>()
const failures = new Map<() => void, unknown>()
const cancellations = new Map<string, Set<() => void>>()

/** An explicitly deleted target must not be recreated by a failed write's retry. */
export function cancelPersisted(identity: string) {
  for (const cancel of cancellations.get(identity) ?? []) cancel()
  cancellations.delete(identity)
}

/** Serialize and write every store with unsaved changes now. */
export function flushPersisted() {
  for (const save of [...pending]) save()
}

/** Wait for pending and in-flight storage writes before an intentional page reload. */
export async function flushPersistedAsync() {
  do {
    flushPersisted()
    await Promise.all([...writing])
    if (failures.size) throw new AggregateError([...failures.values()], "Pending changes could not be saved.")
  } while (pending.size || writing.size)
}

// Covers synchronous web storage. Desktop registers its own pagehide handling earlier than this
// module loads, so its shutdown path calls flushPersisted() itself before flushing namespaces.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPersisted()
  })
  window.addEventListener("pagehide", flushPersisted)
}

// A store whose serialized form is written to storage on a schedule instead of on every setter
// call. The setter only marks the store dirty; serialization happens once per save window, once
// per owner cleanup, and when the page hides. Mirrors VS Code's Memento.
export function persistStore<T extends object>(input: {
  store: Store<T>
  setStore: SetStoreFunction<T>
  name: string
  identity?: string
  storage: SyncStorage | AsyncStorage
  serialize: (value: T) => string
  deserialize: (raw: string) => T
  sync?: PersistenceSyncAPI
  /** App-global preferences may arrive from a window showing a different route. */
  syncAcrossUrls?: boolean
  delay?: number
  /** Replaces `storage.setItem` for stores whose storage can take the value itself. */
  write?: (value: T, serialized: string) => void | Promise<void>
}) {
  const delay = input.delay ?? persistSaveDelay
  let dirty = false
  let touched = false
  let last: string | undefined
  // The newest value another window wrote while this store was dirty; applied at save time if the
  // local setter calls turned out not to change anything.
  let remote: string | undefined
  let timer: ReturnType<typeof setTimeout> | undefined
  let attempt = 0
  let cancelled = false
  const operations = new Set<Promise<void>>()
  const cancel = () => {
    cancelled = true
    attempt++
    clearTimeout(timer)
    pending.delete(save)
    failures.delete(save)
    for (const operation of operations) writing.delete(operation)
    operations.clear()
  }
  const track = () => {
    if (input.identity === undefined) return
    const callbacks = cancellations.get(input.identity) ?? new Set<() => void>()
    callbacks.add(cancel)
    cancellations.set(input.identity, callbacks)
  }
  const release = () => {
    if (input.identity === undefined || dirty || operations.size) return
    const callbacks = cancellations.get(input.identity)
    callbacks?.delete(cancel)
    if (!callbacks?.size) cancellations.delete(input.identity)
  }

  const save = () => {
    clearTimeout(timer)
    timer = undefined
    if (cancelled || !dirty) return
    const next = untrack(() => input.serialize(input.store))
    pending.delete(save)
    dirty = false
    const held = remote
    remote = undefined
    if (next === last && !failures.has(save)) {
      if (held !== undefined && held !== last) hydrate(held)
      release()
      return
    }
    last = next
    const version = ++attempt
    const failed = (error: unknown) => {
      if (version !== attempt) return
      failures.set(save, error)
      last = undefined
      dirty = true
      pending.add(save)
    }
    try {
      input.sync?.[1](input.name, next)
      const result = input.write ? input.write(input.store, next) : input.storage.setItem(input.name, next)
      if (!(result instanceof Promise)) {
        failures.delete(save)
        release()
        return
      }
      const operation = result.then(
        () => {
          writing.delete(operation)
          operations.delete(operation)
          if (version === attempt) failures.delete(save)
          release()
        },
        (error: unknown) => {
          writing.delete(operation)
          operations.delete(operation)
          failed(error)
          release()
          console.error(`[persistence] write failed for ${input.name}`, error)
        },
      )
      writing.add(operation)
      operations.add(operation)
    } catch (error) {
      failed(error)
      throw error
    }
  }

  // Solid's setter overloads are too deep to spread generically; the wrapper only forwards.
  const apply = input.setStore as unknown as (...values: unknown[]) => void
  const setStore = ((...values: unknown[]) => {
    apply(...values)
    if (cancelled) return
    dirty = true
    touched = true
    pending.add(save)
    track()
    timer ??= setTimeout(save, delay)
  }) as unknown as SetStoreFunction<T>

  const hydrate = (raw: string) => {
    last = raw
    input.setStore(reconcile(input.deserialize(raw)))
  }
  const init = input.storage.getItem(input.name)
  // A value the user already changed is newer than whatever storage held.
  if (init instanceof Promise) void init.then((raw) => raw && !touched && hydrate(raw))
  else if (init) hydrate(init)

  input.sync?.[0]((data) => {
    if (data.key !== input.name) return
    if (!input.syncAcrossUrls && (data.url ?? location.href) !== location.href) return
    if (!data.newValue) return
    // A real unsaved local change wins over another window's write, as in VS Code's storage
    // service; whether the change is real is only known when the store is serialized. Every
    // remote value replaces the held one, including a revert to `last`, so the save sees the
    // other window's final state rather than an intermediate one.
    if (dirty) {
      remote = data.newValue
      return
    }
    if (data.newValue === last) return
    hydrate(data.newValue)
  })

  if (getOwner()) onCleanup(save)

  return { setStore, init, flush: save }
}
